<script lang="ts">
  import { onDestroy, onMount } from "svelte";
  import {
    updateUrlParam,
    selectedOtherActivity,
    isOtherActivitiesWindowOpen,
    selectedActivityIndex,
  } from "../store/AppState";
  import { otherActivities } from "../config/other-activities";
  import type { OtherActivity } from "../declarations/config";
  import isMobile from "is-mobile";
  import {
    INTERACTION_TYPE_SIGNPOST,
    sendInteractionEvent,
  } from "../utils/analytics";

  let Modal: HTMLElement;
  let OtherActivitiesWindow: HTMLElement;

  const setClosingStyles = () => {
    if (Modal !== null && OtherActivitiesWindow !== null) {
      Modal.style.width = "1dvw";
      Modal.style.height = "1dvh";
      Modal.style.top = "50%";
      Modal.style.left = "50%";
      Modal.style.right = "50%";
      Modal.style.bottom = "50%";
      OtherActivitiesWindow.classList.add("hide");
    }
  };

  const keydownListener = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      handleWindowClose();
    }
  };

  const handleActivityClose = () => {
    updateUrlParam("aktivita", null);
    $selectedOtherActivity = null;
  };

  const handleWindowClose = () => {
    handleActivityClose();

    setClosingStyles();
    setTimeout(() => {
      $isOtherActivitiesWindowOpen = false;
      $selectedOtherActivity = null;
    }, 300);
  };

  onMount(() => {
    $selectedActivityIndex = null;
    const body = document.getElementById("body");
    if (body) {
      body.classList.add("scroll-stopper");
    }
    window.addEventListener("keydown", keydownListener);
    setTimeout(() => {
      if (Modal !== null && OtherActivitiesWindow !== null) {
        Modal.style.width = "100dvw";
        Modal.style.height = "100dvh";
        Modal.style.top = "0";
        Modal.style.left = "0";
        Modal.style.right = "0";
        Modal.style.bottom = "0";
        OtherActivitiesWindow.classList.remove("hide");
      }
    }, 100);
  });

  const handleActivitySelection = (activity: OtherActivity) => {
    sendInteractionEvent(INTERACTION_TYPE_SIGNPOST, activity.slug);
    if (!["soutez", "o-projektu"].includes(activity.slug)) {
      window.open(`${activity.src}`, "_blank");
      return;
    }
    $selectedOtherActivity = activity.order;
    updateUrlParam("aktivita", activity.slug);
  };

  onDestroy(() => {
    const body = document.getElementById("body");
    if (body) {
      body.classList.remove("scroll-stopper");
    }
    window.removeEventListener("keydown", keydownListener);
  });
</script>

<div
  bind:this={Modal}
  class="modal-window"
  style={`--alignItems: ${
    isMobile({ tablet: true }) ? "" : "center"
  }; --zIndex: ${isMobile({ tablet: true }) ? "1000002" : "100000"}`}
>
  <div
    class="modal-box"
    style={`--margin: ${isMobile({ tablet: true }) ? "" : "auto"}; --width: ${
      isMobile({ tablet: true }) ? "100%" : "80%"
    }; --height: ${isMobile({ tablet: true }) ? "100%" : "80%"}`}
  >
    <div
      class="modal-header"
      style={`--border-radius: ${
        isMobile({ tablet: true }) ? "0" : "1rem"
      }; --padding: ${isMobile({ tablet: true }) ? "1rem" : "0.5rem"};`}
    >
      {#if $selectedOtherActivity !== null}
        <button class="back-button" on:pointerup={handleActivityClose}>
          <img src={`${__PUBLIC_PATH__}images/icon-back.svg`} alt="back" />
        </button>
      {/if}
      <button
        class="close-button"
        style={`--top: ${isMobile({ tablet: true }) ? "0.5rem" : "1rem"}`}
        on:pointerup={handleWindowClose}
        ><img
          src={`${__PUBLIC_PATH__}images/icon-close-black.svg`}
          alt="close"
        /></button
      >
    </div>
    <div
      class="modal-content"
      style={`--height: ${
        isMobile({ tablet: false })
          ? "calc(100% - 11.5rem)"
          : isMobile({ tablet: true })
            ? "100dvh"
            : "100%"
      }`}
    >
      <div
        bind:this={OtherActivitiesWindow}
        class="other-activities-window hide"
        style={`--height: ${
          isMobile({ tablet: true })
            ? "100%"
            : $selectedOtherActivity !== null
              ? "80%"
              : "auto"
        }`}
      >
        {#if $selectedOtherActivity !== null}
          <iframe
            class="iframe"
            src={otherActivities[$selectedOtherActivity - 1].src}
            allow="autoplay"
            sandbox="allow-presentation allow-popups-to-escape-sandbox allow-top-navigation-by-user-activation allow-same-origin allow-scripts allow-popups allow-forms"
            allowfullscreen
            title="modal-iframe"
            width="100%"
            height="100%"
          />
        {:else}
          <div class="other-activities">
            {#each otherActivities as activity}
              <button
                on:pointerup={() => handleActivitySelection(activity)}
                class="other-activity"
              >
                <img
                  alt="activity-thumbnail"
                  src={`${__PUBLIC_PATH__}images/${activity.thumbnail}`}
                />
                <div class="activity-text">
                  <div>
                    <span>{activity.title}</span>
                    {#if !["soutez", "o-projektu"].includes(activity.slug)}
                      <img
                        class="outer-link"
                        alt="outer-link"
                        src={`${__PUBLIC_PATH__}images/outer-link.svg`}
                      />
                    {/if}
                  </div>
                  <p>{activity.description}</p>
                </div>
              </button>
            {/each}
          </div>
        {/if}
      </div>
    </div>
  </div>
</div>

<style>
  .modal-window {
    position: fixed;
    top: 50%;
    left: 50%;
    bottom: 50%;
    right: 50%;
    width: 1dvw;
    height: 1dvh;
    z-index: var(--zIndex);
    background: hsla(208, 42%, 17%, 0.8);
    display: flex;
    justify-content: center;
    align-items: var(--alignItems);
    transition: 0.3s ease-in;
  }

  .modal-box {
    max-height: 900px;
    max-width: 1600px;
    position: relative;
    flex-shrink: 0;
    margin: var(--margin);
    height: var(--height);
    width: var(--width);
  }

  .modal-content {
    position: relative;
    height: var(--height);
    font-size: 0rem;
    color: white;
    text-align: left;
  }

  .iframe {
    position: absolute;
    height: 100%;
    width: 100%;
    left: 0;
    top: 0;
    width: 100%;
    border: none;
  }

  .modal-header {
    height: 3.5rem;
    width: 100%;
    padding-top: var(--padding);
    padding-bottom: var(--padding);
    border-top-left-radius: var(--border-radius);
    border-top-right-radius: var(--border-radius);
    position: absolute;
    background-color: hsla(204, 67%, 94%, 1);
  }

  .back-button {
    all: unset;
    position: absolute;
    left: 1rem;
    top: 1rem;
    bottom: 1rem;
    cursor: pointer;
    z-index: 2;
  }

  .close-button {
    all: unset;
    position: absolute;
    right: 1rem;
    top: var(--top);
    bottom: 1rem;
    cursor: pointer;
    z-index: 2;
  }

  .back-button img,
  .close-button img {
    height: 2.5rem;
    width: 2.5rem;
  }

  .other-activities-window {
    height: var(--height);
    overflow-y: auto;
    background-color: hsla(0, 0%, 100%, 1);
    border-bottom-left-radius: 1rem;
    border-bottom-right-radius: 1rem;
    position: absolute;
    width: 100%;
    left: 0;
    top: 4.5rem;
    border: none;
    transition: 0.3s ease-in;
    padding-top: 3rem;
    padding-bottom: 3rem;
  }

  .other-activities {
    max-height: 80%;
    padding-left: 3rem;
    padding-right: 3rem;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    row-gap: 4.5rem;
    column-gap: 3rem;
    align-items: flex-start;
    align-self: stretch;
    align-content: flex-start;
  }

  .other-activity {
    all: unset;
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: 1rem;
    flex: 1 0 0;
    min-width: 16.25rem;
    flex-basis: 25%;
    cursor: pointer;
  }

  .other-activity img {
    height: 7.5rem;
    width: 7.5rem;
    transition: 0.3s ease-in;
  }

  .activity-text {
    display: flex;
    flex-direction: column;
    margin-left: 1rem;
    align-items: flex-start;
    align-content: flex-start;
  }

  .other-activity span {
    all: unset;
    font-size: 2.25rem;
    font-style: normal;
    font-weight: 700;
    line-height: 2.8125rem;
    color: hsla(19, 100%, 37%, 1);
    font-family: "Cernobil2021";
    transition: 0.3s ease-in;
  }

  .other-activity p {
    font-size: 1.6875rem;
    font-family: "source-sans-pro", sans-serif;
    font-weight: 400;
    font-style: normal;
    line-height: 2.25rem;
    color: hsla(208, 42%, 17%, 1);
    margin: 0;
    margin-top: 1rem;
    white-space: wrap;
    transition: 0.3s ease-in;
  }

  .other-activity:last-of-type {
    padding-bottom: 3rem;
  }

  .hide img {
    height: 0rem !important;
    width: 0rem !important;
  }

  .hide p {
    font-size: 0rem !important;
  }

  .hide span {
    font-size: 0rem !important;
  }

  img.outer-link {
    width: unset;
    height: unset;
    margin-left: 0.25rem;
    vertical-align: top;
  }
</style>
